<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { computedAsync } from '@vueuse/core';

import { useBrowserStore } from '@apps/main/new-desktop/core/stores';

import { performanceMark } from '@libs/utils';
import { isExperimentAsync } from '@libs/feature-toggle';

import {
  ItinerarySegmentType,
  isItineraryLocationSegment,
  isItinerarySimpleTransportSegment,
} from '../../models';
import { useTripOverviewPageStore } from '../../stores';
import { GlobalSuggestionTypeSwitch } from '../globalSuggestionType';

import AddDestinationButton from './AddDestinationButton.vue';
import ItineraryDateRange from './ItineraryDateRange.vue';
import ItineraryNoTransportSegment from './ItineraryNoTransportSegment.vue';
import ItineraryUpsellNoTransportSegment from './ItineraryUpsellNoTransportSegment.vue';
import ItinerarySimpleTransportSegment from './ItinerarySimpleTransportSegment.vue';
import ItineraryAccommodationSegment from './accommodation/ItineraryAccommodationSegment.vue';
import ItineraryActivitySegment from './acitivity/ItineraryActivitySegment.vue';
import ItineraryFerrySegment from './ferry/ItineraryFerrySegment.vue';
import ItineraryFlightSegment from './flight/ItineraryFlightSegment.vue';
import ItineraryLocationSegment from './location/ItineraryLocationSegment.vue';
import ItineraryUpsellSegment from './upsell/ItineraryUpsellSegment.vue';
import ItineraryOwnStaySegment from './ownStay/ItineraryOwnStaySegment.vue';
import ItineraryStenaUpsellNoTransportSegment from './ItineraryStenaUpsellNoTransportSegment.vue';

interface TripItineraryProps {
  printMode: boolean;
}

const props = defineProps<TripItineraryProps>();

const tripOverviewPageStore = useTripOverviewPageStore();

const browserStore = useBrowserStore();

const isHideOverviewTimelineExperiment = computedAsync<boolean>(
  () => isExperimentAsync('hide_overview_timeline'),
  false
);

const shouldShowGlobalSuggestionSwitch = computed<boolean>(
  () =>
    browserStore.isSmallerThan600px || isHideOverviewTimelineExperiment.value
);

onMounted(() => {
  if (tripOverviewPageStore.isFirstTripItineraryRender) {
    performanceMark('FirstItineraryRendered');
    tripOverviewPageStore.isFirstTripItineraryRender = false;
  }
});
</script>

<template>
  <div class="trip-itinerary">
    <div v-if="shouldShowGlobalSuggestionSwitch" class="global-suggestion">
      <GlobalSuggestionTypeSwitch />
    </div>
    <div
      v-for="(segment, index) in tripOverviewPageStore.tripItinerary"
      :key="index"
      :class="{
        'is-hide-timeline-experiment':
          isHideOverviewTimelineExperiment && index === 0,
      }"
    >
      <ItineraryDateRange
        v-if="
          segment.segmentType === ItinerarySegmentType.DateRange &&
          !isHideOverviewTimelineExperiment
        "
        :relative-days="segment.time.relativeDays"
        :absolute-short-days="segment.time.absoluteShortDays"
        :is-first-segment="index === 0"
        :second-segment-is-no-transport="
          tripOverviewPageStore.tripItinerary[1]?.segmentType ===
            ItinerarySegmentType.NoTransport ||
          tripOverviewPageStore.tripItinerary[1]?.segmentType ===
            ItinerarySegmentType.UpsellNoTransport ||
          tripOverviewPageStore.tripItinerary[1]?.segmentType ===
            ItinerarySegmentType.StenaUpsellNoTransport
        "
      />

      <ItineraryLocationSegment
        v-if="isItineraryLocationSegment(segment)"
        :segment="segment"
        :print-mode="props.printMode"
      />

      <ItineraryNoTransportSegment
        v-if="
          segment.segmentType === ItinerarySegmentType.NoTransport &&
          !props.printMode
        "
        :segment="segment"
        :print-mode="props.printMode"
      />

      <ItineraryUpsellNoTransportSegment
        v-if="
          segment.segmentType === ItinerarySegmentType.UpsellNoTransport &&
          !props.printMode
        "
        :segment="segment"
      />

      <ItineraryStenaUpsellNoTransportSegment
        v-if="
          segment.segmentType === ItinerarySegmentType.StenaUpsellNoTransport &&
          !props.printMode
        "
        :segment="segment"
      />

      <ItinerarySimpleTransportSegment
        v-if="isItinerarySimpleTransportSegment(segment)"
        :segment="segment"
        :print-mode="props.printMode"
      />

      <ItineraryAccommodationSegment
        v-if="segment.segmentType === ItinerarySegmentType.Accommodation"
        :segment="segment"
        :print-mode="props.printMode"
      />

      <ItineraryOwnStaySegment
        v-if="segment.segmentType === ItinerarySegmentType.OwnStay"
        :segment="segment"
      />

      <ItineraryActivitySegment
        v-if="segment.segmentType === ItinerarySegmentType.Activity"
        :segment="segment"
        :print-mode="props.printMode"
      />

      <ItineraryFerrySegment
        v-if="segment.segmentType === ItinerarySegmentType.Ferry"
        :segment="segment"
        :print-mode="props.printMode"
      />

      <ItineraryFlightSegment
        v-if="segment.segmentType === ItinerarySegmentType.Flight"
        :segment="segment"
        :print-mode="props.printMode"
      />

      <ItineraryUpsellSegment
        v-else-if="
          segment.segmentType === ItinerarySegmentType.Upsell &&
          !props.printMode
        "
        :data="segment.productCardData"
        :segment-type="segment.segmentType"
      />
    </div>

    <AddDestinationButton v-if="!props.printMode" />
  </div>
</template>

<style scoped lang="scss">
.is-hide-timeline-experiment {
  margin-top: var(--spacing-24);
}

.global-suggestion {
  width: fit-content;
  margin: var(--spacing-24) auto 0;
}
</style>
